import { useState, useEffect } from 'react';

function getElBySelector(
	id: string = '',
	myDocument?: Document
): HTMLElement | null {
	return myDocument !== undefined
		? (myDocument.getElementById(id) as HTMLElement)
		: (document.getElementById(id) as HTMLElement);
}

/**
 *
 * @example https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
 */
function waitForDomNode(
	selector: string = '',
	myDocument?: Document
): Promise<HTMLElement | null> {
	// eslint-disable-next-line consistent-return
	return new Promise((resolve) => {
		if (getElBySelector(selector, myDocument)) {
			// eslint-disable-next-line no-promise-executor-return
			return resolve(getElBySelector(selector, myDocument));
		}

		const observer = new MutationObserver(() => {
			if (getElBySelector(selector, myDocument)) {
				resolve(getElBySelector(selector, myDocument));
				observer.disconnect();
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	});
}

/**
 *
 * @example https://github.com/facebook/react/issues/14326#issuecomment-441680293
 */
export function useGetDomNodeWithMutationObserver({
	selector = '', // string for ID (target-id) not with hash (#target-id)
	myDocument = undefined,
}: {
	selector: string;
	myDocument?: Document;
}) {
	const [domNode, setDomNode] = useState<HTMLElement | null>(null);

	useEffect(() => {
		async function getDomNode() {
			const response = await waitForDomNode(selector, myDocument);
			setDomNode(response);
		}

		getDomNode();
	}, [selector]);

	return domNode;
}
